import api from './api'
import type { PaginatedResponse } from '@/types'
import type { ApiResponse } from '@/types/api'
import type { AgentDefinition } from '@/stores/agentCatalogStore'

export interface CreateAgentPayload {
  name: string
  supervisor_name?: string
  persona_name?: string
  personality?: string
  system_prompt?: string
  knowledge_base?: string
  knowledge_sources?: unknown[]
  subagents?: unknown[]
  followups?: unknown[]
  metadata?: Record<string, unknown>
  enabled?: boolean
  memory_enabled?: boolean
  strict_logic_flow?: boolean
}

export type UpdateAgentPayload = Partial<CreateAgentPayload>

export interface FollowUpPreviewResult {
  eligible: number
  with_inbound: number
  without_inbound: number
}

export interface SkillVariable {
  key: string
  label: string
  description: string
  example: string
}

export interface SkillVariableCategory {
  id: string
  label: string
  variables: SkillVariable[]
}

const AgentsService = {
  async list(): Promise<AgentDefinition[]> {
    const response = await api.get<PaginatedResponse<AgentDefinition>>('/api/v1/agents')
    return response.data.data
  },

  async listSkillVariables(): Promise<SkillVariableCategory[]> {
    const response = await api.get<ApiResponse<{ categories: SkillVariableCategory[] }>>(
      '/api/v1/agents/skill-variables',
    )
    return response.data.data.categories ?? []
  },

  async previewFollowup(ruleConfig: Record<string, unknown>): Promise<FollowUpPreviewResult> {
    const response = await api.post<ApiResponse<FollowUpPreviewResult>>(
      '/api/v1/agents/followup/preview',
      { rule_config: ruleConfig },
    )
    return response.data.data
  },

  async get(agentId: string): Promise<AgentDefinition> {
    const response = await api.get<ApiResponse<AgentDefinition>>(`/api/v1/agents/${agentId}`)
    return response.data.data
  },

  async create(payload: CreateAgentPayload): Promise<AgentDefinition> {
    const response = await api.post<ApiResponse<AgentDefinition>>('/api/v1/agents', payload)
    return response.data.data
  },

  async update(agentId: string, payload: UpdateAgentPayload): Promise<AgentDefinition> {
    const response = await api.put<ApiResponse<AgentDefinition>>(
      `/api/v1/agents/${agentId}`,
      payload,
    )
    return response.data.data
  },

  async delete(agentId: string): Promise<void> {
    await api.delete(`/api/v1/agents/${agentId}`)
  },
}

export default AgentsService
